import { createTheme, alpha, type Theme } from "@mui/material/styles";
import type { ThemeConfig, GlassmorphismStyles } from "./types";
import { glassmorphismVariants } from "./glassmorphism.config";
import { appColors } from "./colors";

// Use centralized color palette
const macOS26Colors = {
  primary: appColors.primary,
  secondary: appColors.secondary,
  success: appColors.success,
  warning: appColors.warning,
  error: appColors.error,
  info: appColors.info,
  ai: appColors.ai,
  background: {
    default: appColors.background.light.gradient,
    paper: appColors.background.light.paper
  },
  text: appColors.text.light,
  divider: appColors.border.light.default,
  action: {
    active: appColors.primary.rgba["0.15"],
    hover: "rgba(255, 255, 255, 0.2)",
    selected: appColors.primary.rgba["0.25"],
    disabled: "rgba(150, 150, 150, 0.3)",
    disabledBackground: "rgba(200, 200, 200, 0.1)"
  }
};

// Glassmorphism styles for the light theme (via config)
export const glassmorphismLight: GlassmorphismStyles = {
  ...glassmorphismVariants.light.default,
  borderRadius: "16px"
};

// Glassmorphism styles for the dark theme (via config)
export const glassmorphismDark: GlassmorphismStyles = {
  ...glassmorphismVariants.dark.default,
  borderRadius: "16px"
};

// Extra variants for different components
export const glassmorphismLightCard = glassmorphismVariants.light.card;
export const glassmorphismLightPanel = glassmorphismVariants.light.panel;
export const glassmorphismLightSubtle = glassmorphismVariants.light.subtle;
export const glassmorphismLightStrong = glassmorphismVariants.light.strong;

export const glassmorphismDarkCard = glassmorphismVariants.dark.card;
export const glassmorphismDarkPanel = glassmorphismVariants.dark.panel;
export const glassmorphismDarkSubtle = glassmorphismVariants.dark.subtle;
export const glassmorphismDarkStrong = glassmorphismVariants.dark.strong;

export const createMacOS26Theme = (mode: "light" | "dark" = "light"): Theme => {
  const isLight = mode === "light";
  const glassCard = isLight ? glassmorphismLightCard : glassmorphismDarkCard;
  const glassPanel = isLight ? glassmorphismLightPanel : glassmorphismDarkPanel;

  return createTheme({
    palette: {
      mode,
      primary: macOS26Colors.primary,
      secondary: macOS26Colors.secondary,
      success: macOS26Colors.success,
      warning: macOS26Colors.warning,
      error: macOS26Colors.error,
      info: macOS26Colors.info,
      background: {
        default: isLight
          ? appColors.background.light.default
          : appColors.background.dark.default,
        paper: isLight
          ? appColors.background.light.paper
          : appColors.background.dark.paper
      },
      text: {
        primary: isLight ? appColors.text.light.primary : appColors.text.dark.primary,
        secondary: isLight ? appColors.text.light.secondary : appColors.text.dark.secondary,
        disabled: isLight ? appColors.text.light.disabled : appColors.text.dark.disabled
      },
      divider: isLight ? appColors.border.light.default : appColors.border.dark.default,
      action: {
        ...macOS26Colors.action,
        hover: isLight ? "rgba(255, 255, 255, 0.2)" : "rgba(255, 255, 255, 0.1)",
        selected: isLight ? appColors.primary.rgba["0.25"] : appColors.primary.rgba["0.3"]
      }
    },
    shape: {
      borderRadius: 12
    },
    typography: {
      fontFamily: [
        "Inter",
        "-apple-system",
        "BlinkMacSystemFont",
        '"SF Pro Display"',
        '"SF Pro Text"',
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif"
      ].join(","),
      h1: {
        fontWeight: 700,
        letterSpacing: "-0.03em",
        lineHeight: 1.2
      },
      h2: {
        fontWeight: 700,
        letterSpacing: "-0.02em",
        lineHeight: 1.3
      },
      h3: {
        fontWeight: 700,
        letterSpacing: "-0.01em",
        lineHeight: 1.3
      },
      h4: {
        fontWeight: 600,
        letterSpacing: "-0.01em",
        lineHeight: 1.4
      },
      h5: {
        fontWeight: 600,
        letterSpacing: "0em",
        lineHeight: 1.4
      },
      h6: {
        fontWeight: 600,
        letterSpacing: "0.01em",
        lineHeight: 1.5
      },
      button: {
        textTransform: "none",
        fontWeight: 600,
        letterSpacing: "0.01em"
      },
      body1: {
        fontWeight: 400,
        lineHeight: 1.6
      },
      body2: {
        fontWeight: 400,
        lineHeight: 1.5
      }
    },
    shadows: [
      "none",
      "0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)",
      "0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06)",
      "0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)",
      "0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)",
      "0 25px 50px rgba(0, 0, 0, 0.15)",
      "0 25px 50px rgba(0, 0, 0, 0.15)",
      "0 25px 50px rgba(0, 0, 0, 0.15)",
      "0 25px 50px rgba(0, 0, 0, 0.15)",
      "0 25px 50px rgba(0, 0, 0, 0.15)",
      "0 25px 50px rgba(0, 0, 0, 0.15)",
      "0 25px 50px rgba(0, 0, 0, 0.15)",
      "0 25px 50px rgba(0, 0, 0, 0.15)",
      "0 25px 50px rgba(0, 0, 0, 0.15)",
      "0 25px 50px rgba(0, 0, 0, 0.15)",
      "0 25px 50px rgba(0, 0, 0, 0.15)",
      "0 25px 50px rgba(0, 0, 0, 0.15)",
      "0 25px 50px rgba(0, 0, 0, 0.15)",
      "0 25px 50px rgba(0, 0, 0, 0.15)",
      "0 25px 50px rgba(0, 0, 0, 0.15)",
      "0 25px 50px rgba(0, 0, 0, 0.15)",
      "0 25px 50px rgba(0, 0, 0, 0.15)",
      "0 25px 50px rgba(0, 0, 0, 0.15)",
      "0 25px 50px rgba(0, 0, 0, 0.15)",
      "0 25px 50px rgba(0, 0, 0, 0.15)"
    ] as Theme["shadows"],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            background: isLight
              ? appColors.background.light.gradient
              : appColors.background.dark.gradient,
            backgroundAttachment: "fixed",
            minHeight: "100vh"
          },
          body: {
            background: isLight
              ? appColors.background.light.gradient
              : appColors.background.dark.gradient,
            backgroundAttachment: "fixed",
            minHeight: "100vh",
            margin: 0,
            padding: 0
          },
          "#root": {
            minHeight: "100vh",
            background: "rgba(0, 0, 0, 0)"
          },
          "*": {
            scrollbarWidth: "thin",
            scrollbarColor: `${isLight ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.2)"} transparent`
          },
          "*::-webkit-scrollbar": {
            width: "8px",
            height: "8px"
          },
          "*::-webkit-scrollbar-track": {
            background: "rgba(0, 0, 0, 0)"
          },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: isLight ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.2)",
            borderRadius: "4px",
            "&:hover": {
              backgroundColor: isLight ? "rgba(0,0,0,0.3)" : "rgba(255,255,255,0.3)"
            }
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            ...glassPanel,
            "&.MuiPaper-elevation": {
              ...glassPanel
            },
            "&.MuiPaper-rounded": {
              borderRadius: glassPanel.borderRadius
            }
          },
          elevation0: {
            boxShadow: "none",
            border: glassPanel.border
          },
          elevation1: {
            ...glassPanel
          },
          elevation2: {
            ...glassPanel,
            boxShadow: glassCard.boxShadow
          },
          elevation3: {
            ...glassCard
          },
          elevation4: {
            ...glassCard
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            ...glassCard,
            color: isLight ? "rgba(30, 30, 30, 0.9)" : "rgba(255, 255, 255, 0.95)",
            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: "translateY(-4px) scale(1.01)",
              boxShadow: isLight
                ? "0 16px 48px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.7) inset"
                : "0 16px 48px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.2) inset"
            }
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: "12px",
            padding: "10px 24px",
            fontWeight: 600,
            textTransform: "none",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            letterSpacing: "0.01em",
            "&.Mui-disabled": {
              opacity: 1,
            },
            "&:hover": {
              transform: "translateY(-2px)"
            },
            "&:active": {
              transform: "translateY(0px)"
            }
          },
          contained: {
            background: isLight
              ? `linear-gradient(135deg, ${appColors.primary.main} 0%, ${appColors.secondary.main} 100%)`
              : `linear-gradient(135deg, ${appColors.primary.main} 0%, ${appColors.info.main} 100%)`,
            boxShadow: isLight
              ? `0 8px 24px ${alpha(appColors.primary.main, 0.35)}, 0 0 0 1px rgba(255, 255, 255, 0.3) inset`
              : `0 8px 24px ${alpha(appColors.primary.main, 0.4)}, 0 0 0 1px rgba(255, 255, 255, 0.1) inset`,
            color: "#FFFFFF",
            "&.Mui-disabled": {
              color: "#FFFFFF",
              background: isLight
                ? "linear-gradient(135deg, #7f99ba 0%, #8da6c5 100%)"
                : "linear-gradient(135deg, #4f7fb4 0%, #5f8fc3 100%)",
            },
            "&:hover": {
              background: isLight
                ? `linear-gradient(135deg, ${appColors.primary.light} 0%, ${appColors.secondary.light} 100%)`
                : `linear-gradient(135deg, ${appColors.primary.light} 0%, ${appColors.info.light} 100%)`,
              boxShadow: isLight
                ? `0 12px 32px ${alpha(appColors.primary.main, 0.45)}, 0 0 0 1px rgba(255, 255, 255, 0.4) inset`
                : `0 12px 32px ${alpha(appColors.primary.main, 0.5)}, 0 0 0 1px rgba(255, 255, 255, 0.2) inset`
            }
          },
          outlined: {
            border: isLight
              ? `2px solid rgba(255, 255, 255, 0.6)`
              : `2px solid rgba(255, 255, 255, 0.3)`,
            background: isLight
              ? "rgba(255, 255, 255, 0.25)"
              : "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(15px)",
            WebkitBackdropFilter: "blur(15px)",
            color: isLight ? "rgba(30, 30, 30, 0.9)" : "#FFFFFF",
            "&.Mui-disabled": {
              color: isLight ? "rgba(30, 30, 30, 0.7)" : "rgba(255, 255, 255, 0.75)",
              border: isLight ? "2px solid rgba(30, 30, 30, 0.25)" : "2px solid rgba(255, 255, 255, 0.3)",
              background: isLight ? "rgba(255, 255, 255, 0.3)" : "rgba(255, 255, 255, 0.08)",
            },
            "&:hover": {
              background: isLight
                ? "rgba(255, 255, 255, 0.35)"
                : "rgba(255, 255, 255, 0.15)",
              border: isLight
                ? `2px solid rgba(255, 255, 255, 0.8)`
                : `2px solid rgba(255, 255, 255, 0.5)`,
              boxShadow: `0 4px 12px ${alpha(macOS26Colors.primary.main, 0.25)}`
            }
          },
          text: {
            color: isLight ? "rgba(30, 30, 30, 0.9)" : "#FFFFFF",
            "&.Mui-disabled": {
              color: isLight ? "rgba(30, 30, 30, 0.6)" : "rgba(255, 255, 255, 0.7)",
            },
            "&:hover": {
              background: isLight
                ? "rgba(255, 255, 255, 0.2)"
                : "rgba(255, 255, 255, 0.1)"
            }
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              background: isLight
                ? "rgba(255, 255, 255, 0.5)"
                : "rgba(255, 255, 255, 0.05)",
              backdropFilter: "blur(25px)",
              WebkitBackdropFilter: "blur(25px)",
              transition: "all 0.3s ease",
              color: isLight ? "rgba(30, 30, 30, 0.9)" : "rgba(255, 255, 255, 0.95)",
              "&:hover": {
                background: isLight
                  ? "rgba(255, 255, 255, 0.6)"
                  : "rgba(255, 255, 255, 0.08)"
              },
              "&.Mui-focused": {
                background: isLight
                  ? "rgba(255, 255, 255, 0.7)"
                  : "rgba(255, 255, 255, 0.1)",
                boxShadow: `0 0 0 4px ${alpha(appColors.primary.main, 0.25)}, 0 0 24px ${alpha(appColors.primary.main, 0.15)}`
              },
              "& fieldset": {
                borderColor: isLight
                  ? appColors.border.light.strong
                  : appColors.border.dark.weak,
                borderWidth: "1.5px"
              },
              "&:hover fieldset": {
                borderColor: isLight
                  ? "rgba(255, 255, 255, 0.8)"
                  : appColors.border.dark.default
              },
              "&.Mui-focused fieldset": {
                borderColor: appColors.primary.main,
                borderWidth: "2px"
              }
            },
            "& .MuiInputBase-input": {
              color: isLight ? appColors.text.light.primary : appColors.text.dark.primary,
              "&::placeholder": {
                color: isLight ? appColors.text.light.secondary : appColors.text.dark.secondary,
                opacity: 1
              }
            },
            "& .MuiInputLabel-root": {
              color: isLight ? appColors.text.light.secondary : appColors.text.dark.secondary,
              "&.Mui-focused": {
                color: appColors.primary.main
              }
            }
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            ...glassPanel,
            border: "none",
            borderBottom: `1px solid ${isLight ? appColors.border.light.strong : appColors.border.dark.default}`,
            boxShadow: isLight
              ? `0 2px 8px ${appColors.shadow.light.default}`
              : `0 2px 8px ${appColors.shadow.dark.default}`,
            color: isLight ? appColors.text.light.primary : appColors.text.dark.primary
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            ...glassPanel,
            border: "none",
            borderRight: `1px solid ${isLight ? appColors.border.light.strong : appColors.border.dark.default}`
          }
        }
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: "10px",
            margin: "2px 8px",
            transition: "all 0.2s ease",
            "&:hover": {
              background: isLight
                ? appColors.primary.rgba["0.15"]
                : "rgba(255, 255, 255, 0.1)",
              transform: "translateX(4px)"
            },
            "&.Mui-selected": {
              background: isLight
                ? appColors.primary.rgba["0.25"]
                : appColors.primary.rgba["0.3"],
              color: isLight ? appColors.primary.main : "#FFFFFF",
              "&:hover": {
                background: isLight
                  ? appColors.primary.rgba["0.3"]
                  : appColors.primary.rgba["0.4"]
              }
            }
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: "16px",
            background: isLight
              ? "rgba(255, 255, 255, 0.45)"
              : "rgba(255, 255, 255, 0.15)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: `1.5px solid ${isLight ? appColors.border.light.strong : appColors.border.dark.default}`,
            fontWeight: 600,
            color: isLight ? appColors.text.light.primary : appColors.text.dark.primary,
            boxShadow: isLight
              ? `0 4px 12px ${appColors.shadow.light.default}`
              : `0 4px 12px ${appColors.shadow.dark.default}`,
            "&:hover": {
              background: isLight
                ? "rgba(255, 255, 255, 0.55)"
                : "rgba(255, 255, 255, 0.2)",
              border: `1.5px solid ${isLight ? "rgba(255, 255, 255, 0.8)" : appColors.border.dark.strong}`,
              boxShadow: isLight
                ? `0 6px 16px ${appColors.shadow.light.strong}`
                : `0 6px 16px ${appColors.shadow.dark.strong}`
            }
          }
        }
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            ...glassPanel,
            marginTop: "8px"
          },
          list: {
            padding: "8px"
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            ...glassCard,
            borderRadius: "20px"
          },
          paperFullScreen: {
            borderRadius: 0
          }
        }
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: isLight
              ? "rgba(30, 30, 30, 0.9)"
              : "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px) saturate(150%)",
            WebkitBackdropFilter: "blur(20px) saturate(150%)",
            borderRadius: "10px",
            padding: "8px 16px",
            fontSize: "12px",
            fontWeight: 500,
            color: isLight ? "rgba(255, 255, 255, 0.95)" : "rgba(30, 30, 30, 0.9)",
            boxShadow: isLight
              ? "0 4px 16px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.1) inset"
              : "0 4px 16px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.2) inset",
            border: isLight
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(255, 255, 255, 0.2)"
          }
        }
      }
    }
  });
};

// Theme configuration export
export const macOS26ThemeConfig: ThemeConfig = {
  id: "macos26",
  name: "Softlavka ERP Premium",
  description: "Next-Generation AI-Powered Enterprise Platform - Premium glassmorphism design with advanced gradient backgrounds and modern UI",
  createTheme: () => createMacOS26Theme("light")
};

export const macOS26DarkThemeConfig: ThemeConfig = {
  id: "macos26-dark",
  name: "Softlavka ERP Premium Dark",
  description: "Next-Generation AI-Powered Enterprise Platform - Premium dark glassmorphism design with advanced gradient backgrounds",
  createTheme: () => createMacOS26Theme("dark")
};

